///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthPosition } from '../../position';
import { MarkerStateEnum } from '../marker-state-enum';
import { markerImages } from './node-view-images';
import { E2eTestUtils } from '../../../../../../testing/e2e-test-utils';
import { LocationViewStateModel } from './location-view-state.model';
import MarkerLabel = google.maps.MarkerLabel;

export class NodeFeatureView {
    private static readonly NOT_ACTIVE_LAYER_OPACITY = 0.3;

    // FIXME as for now we should use deprecated Marker for locations due to performance issues.
    // for more details please see https://issuetracker.google.com/issues/333423690
    private marker: google.maps.Marker;
    private markerIcon: any;

    changeDraggable(value: boolean) {
        if (this.marker && value !== this.marker.getDraggable()) {
            this.marker.setDraggable(value);
        }
    }

    isViewDraggable(): boolean {
        return this?.marker?.getDraggable();
    }

    move(position: GtthPosition) {
        this.marker.setPosition(position.toLatLngLiteral());
    }

    changeTitle(title: string) {
        if (this.marker) {
            this.marker.setTitle(title);
        }
    }

    refreshMarker(locationViewState: LocationViewStateModel) {
        if (this.marker) {
            const newMarkerIcon = this.getMarkerIcon(locationViewState);
            if (this.markerIcon && this.markerIcon.url !== newMarkerIcon.url) {
                this.markerIcon = { ...newMarkerIcon };
                this.marker.setIcon(this.markerIcon);
                if (E2eTestUtils.getShowLineMarkers()) {
                    this.marker.setLabel({
                        text: ' ',
                        className: this.markerIcon
                    } as MarkerLabel);
                }
            }
            this.marker.setOpacity(locationViewState.isInActiveLayer ? 1 : NodeFeatureView.NOT_ACTIVE_LAYER_OPACITY);
        }
    }

    draw(map: google.maps.Map, position: GtthPosition, name: string, locationViewState: LocationViewStateModel): void {
        this.markerIcon = this.getMarkerIcon(locationViewState);

        this.marker = new google.maps.Marker({
            position: position.toLatLngLiteral(),
            title: name,
            clickable: true,
            crossOnDrag: false,
            icon: this.markerIcon,
            map: map,
            zIndex: 1500,
            opacity: locationViewState.isInActiveLayer ? 1 : NodeFeatureView.NOT_ACTIVE_LAYER_OPACITY,
            optimized: true
        });

        if (E2eTestUtils.getShowLineMarkers()) {
            this.marker.setLabel({
                text: ' ',
                className: this.markerIcon
            } as MarkerLabel);
        }
    }

    hide() {
        this.marker.setMap(null);
        this.markerIcon = undefined;
    }

    addListener(eventName: string, callback: any) {
        this.marker.addListener(eventName, callback);
    }

    destroyListeners() {
        google.maps.event.clearInstanceListeners(this.marker);
    }

    private getMarkerIcon(locationViewState: LocationViewStateModel): any {
        const svgImage = this.getMarkerStateClass(locationViewState);

        return {
            url: markerImages[svgImage].url,
            size: new google.maps.Size(markerImages[svgImage].size, markerImages[svgImage].size),
            anchor: new google.maps.Point(markerImages[svgImage].size / 2, markerImages[svgImage].size / 2)
        };
    }

    private getMarkerStateClass(locationViewState: LocationViewStateModel): string {
        let markerState = '';

        switch (locationViewState.layerIndex) {
            case 0:
                markerState += 'circle';
                break;
            case 1:
                markerState += 'square';
                break;
            case 2:
                markerState += 'rhombus';
                break;
        }

        if (locationViewState.isPop) {
            markerState += '-pop';
        }

        if (locationViewState.isOptionalBu && !locationViewState.hasLinks) {
            markerState += '-optional';
        } else if (locationViewState.hasLinks && locationViewState.isEnable) {
            if (locationViewState.isHasSector) {
                markerState += '-bu';
            } else {
                markerState += '-tu';
            }
        } else {
            markerState += '-node';
        }

        switch (locationViewState.markerType) {
            case MarkerStateEnum.SELECTED:
                markerState += '-selected';
                break;
            case MarkerStateEnum.DEFAULT:
                if (!locationViewState.isEnable) {
                    markerState += '-disabled';
                }
                break;
        }

        return markerState;
    }
}
